import { Box, Button, ButtonGroup, CircularProgress, Drawer, IconButton, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import CustomTable from '../../../components/common/custom-table/CustomTable'
import { formatDate, Icons } from '../../../utils/utilities'
import CountCard from '../../../components/common/CountCard'
import simbaImg from '../../../assets/simba-copy.png'
import api from '../../../service/api'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import TryAgainMessageBlock from '../../../components/common/TryAgainMessageBlock'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import BreadcrumbList from '../../../components/common/BreadcrumbList'
import CustomTabs from '../../../components/common/CustomTabs'
import noParticipationImg from '../../../assets/no-details.svg'
import CreateOrEditPracticeSessionDialog from '../../../components/presenter/practice-with-simba/CreatePracticeSession'
import SimbaAIForFaculty from '../../../components/ai-mentor/SimbaAIForFaculty'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import CustomTooltip from '../../../components/common/CustomTooltip'
import writeXlsxFile from 'write-excel-file'
import EditPracticeSession from '../../../components/presenter/practice-with-simba/EditPracticeSession'
import dayjs from 'dayjs'
import CustomChip from '../../../components/common/CustomChip'
import ConfirmDialog from '../../../components/common/ConfirmDialog'

const participationReport = async ( data, title, legacyGroup ) => {
    const head = [
        { fontWeight: 'bold', value: "Sl.No.", width: 10, align: "center", borderColor: "#000000" },
        { fontWeight: 'bold', value: "AUID", width: 15, borderColor: "#000000" },
        { fontWeight: 'bold', value: "USN", width: 15, borderColor: "#000000" },
        { fontWeight: 'bold', value: "Student name", width: 40, borderColor: "#000000" },
        { fontWeight: 'bold', value: "Max marks", width: 12, align: "center", borderColor: "#000000" },
        { fontWeight: 'bold', value: "Best Score", width: 12, align: "center", borderColor: "#000000" },
        { fontWeight: 'bold', value: "Average Score", width: 15, align: "center", borderColor: "#000000" },
        { fontWeight: 'bold', value: "Attempts", width: 12, align: "center", borderColor: "#000000" },
    ]
    let rows = [head]
    for ( let i = 0; i < data.length; i++ ) {
        const participation = data[i]
        const backgroundColor = participation.no_attempt ? '#ff5555' : undefined
        let row = []
        row.push( { value: i + 1, type: Number, align: "center", backgroundColor } )
        row.push( { value: participation.student_id, type: String, backgroundColor } )
        row.push( { value: participation.usn, type: String, backgroundColor } )
        row.push( { value: participation?.student_name, type: String, backgroundColor } )
        row.push( { value: +participation.max_score, type: Number, align: "center", backgroundColor } )
        row.push( participation.no_attempt ? { value: "-", type: String, align: "center", backgroundColor } : { value: +participation.best_score, type: Number, align: "center", backgroundColor } )
        row.push( participation.no_attempt ? { value: "-", type: String, align: "center", backgroundColor } : { value: participation.average_score, type: String, align: "center", backgroundColor } )
        row.push( participation.no_attempt ? { value: "Not Attempted", type: String, align: "center", backgroundColor } : { value: +participation.number_of_attempts, type: Number, align: "center", backgroundColor } )
        rows.push( row )
    }

    if ( legacyGroup ) {
        const customMessage = "The batch or section assigned to this practice session belongs to a previous academic year and is no longer active, so the list of students who haven't practiced yet cannot be displayed."

        rows.push( [
            {
                value: customMessage,
                span: head.length,
                fontWeight: "bold",
                backgroundColor: "#FFFF00"
            },
            ...new Array( head.length - 1 ).fill( null ),
        ] )
    }

    const excelData = await writeXlsxFile( rows, { columns: rows[0], buffer: true } )
    const blob = new Blob( [excelData] )
    const a = document.createElement( 'a' )
    a.download = `${title}.xlsx` || `practice-session-report.xlsx`
    a.href = window.URL.createObjectURL( blob )
    a.click()
}

const getAccessGroupDetails = groupDetails => {
    if ( groupDetails.chip_label ) return groupDetails.chip_label
    let data = ""

    if ( groupDetails.entity === 'batch' ) {
        data += `Batch: ${groupDetails.batch_name}${groupDetails.remarks ? `, ${groupDetails.remarks}` : "Remarks: Not given."}`
        return data
    } else {
        data += `Section: ${groupDetails.section_short_name}`
        return data
    }
}

const columns = [
    { id: 1, name: "Sl.No.", field: "index", index: true, width: "80px", maxWidth: "100px", align: "center" },
    { id: 1, name: "Name", field: "student_name", filterable: true, renderCell: attempt => attempt.student_name?.toLowerCase(), cellStyles: { textTransform: "capitalize" }, sortable: true, width: "80px", },
    { id: 1, name: "AUID", field: "student_id", filterable: true, sortable: true, width: "100px", },
    { id: 1, name: "USN", field: "usn", filterable: true, sortable: true, width: "100px", },
    { id: 1, name: "Max Score", field: "max_score", width: "80px", align: "center" },
    { id: 1, name: "Best Score", sortable: true, filterable: true, field: "best_score", width: "80px", align: "center", renderCell: row => row.no_attempt ? "-" : row.best_score },
    { id: 1, name: "Average Score", sortable: true, filterable: true, field: "average_score", width: "100px", align: "center", renderCell: row => row.no_attempt ? "-" : row.average_score },
    { id: 1, name: "Attempts", sortable: true, filterable: true, field: "number_of_attempts", width: "80px", align: "center", renderCell: row => row.no_attempt ? "Not Attempted" : row.number_of_attempts },
]

const rowStylesGetter = row => {
    if ( row.no_attempt ) {
        return {
            "--bg-color": t => t.theme === "dark" ? t.palette.error.dark + "22" : t.palette.error.dark + "44",
            ":hover": {},
            cursor: "default"
        }
    }
    else return {}
}


const PracticeSession = () => {

    const [deleteConfirmationDialogState, setDeleteConfirmationDialogState] = useState( false )
    const [practiceSession, setPracticeSession] = useState( null )
    const [participants, setParticipants] = useState( [] )
    const [assignedStudents, setAssignedStudents] = useState( [] )
    const [legacyGroup, setLegacyGroup] = useState( false )
    const [updating, setUpdating] = useState( false )
    const [editMode, setEditMode] = useState( false )
    const [deleting, setDeleting] = useState( false )
    const [tabValue, setTabValue] = useState( '' )
    const [loading, setLoading] = useState( true )
    const [error, setError] = useState( null )

    const SimbaAIStore = useSimbaAIStore()

    const setSearchParams = useSearchParams().at( 1 )

    const { layout, palette, border, theme } = useTheme()
    const { id } = useParams()

    const navigate = useNavigate()

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const deletePracticeSession = async () => {
        try {
            setDeleting( true )
            await api.simbaAI.deletePracticeSession( practiceSession._id )
            await SimbaAIStore.fetchPracticeSessions()
            navigate( `/faculty/practice-with-simba` )
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while deleting the feature permission rules." )
        } finally {
            setDeleting( false )
        }
    }

    const startQuiz = async () => {
        setSearchParamsValues( { simbaAIOpen: 'true', practiceQuiz: practiceSession._id, simbaAIMode: "practice" } )

        SimbaAIStore.setView( "loading" )
        const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
        if ( success ) {
            SimbaAIStore.setView( "question", "left", false )
        } else
            SimbaAIStore.setView( "practice-session-details-fetch-error", "right", false )
    }

    const fetchAnswerFromAI = async ( topicDetails ) => {

        setSearchParamsValues( { simbaAIOpen: 'true', practiceQuiz: "", simbaAIMode: "learn" } )

        if ( SimbaAIStore.getAiResponsesQueue.request ) {
            SimbaAIStore.addResponse( { topic: SimbaAIStore.getAiResponsesQueue.request, response: SimbaAIStore.getAiResponsesQueue.response } )
            SimbaAIStore.setAiResponsesQueue( { request: "", response: "" } )
        }
        try {
            SimbaAIStore.setView( 'fetching-simba-ai-response', "left", false )
            if ( await SimbaAIStore.getAIResponse( topicDetails ) )
                SimbaAIStore.setView( 'simba-ai-response', "left", false )
            else
                SimbaAIStore.setView( 'simba-ai-response-error', "left", false )
        } catch ( err ) {
            console.log( err )
        }
    }


    const tabs = useMemo( () => {
        if ( !practiceSession ) return []
        const value = [
            { label: "Selected Batches/Sections", icon: Icons.default.Group, value: "assignedgroups", count: practiceSession.access_groups.length },
            // { label: "Reference Materials", icon: Icons.default.FilePresent, value: "refmaterials", count: practiceSession?.reference_materials?.length },
        ]
        if ( practiceSession?.status && practiceSession?.status !== 'draft' ) {
            value.splice( 0, 0, {
                label: "Participants", icon: Icons.default.People, value: "participants", count: Object.keys( participants.reduce( ( acc, attempt ) => {
                    if ( !acc[attempt.student_id] ) acc[attempt.student_id] = true
                    return acc
                }, {} ) ).length
            } )
        }
        return value
    }, [practiceSession, participants] )

    const assignedStudentsByAuid = useMemo( () => {
        const map = {}
        assignedStudents.forEach( student => {
            const key = String( student.auid || student.student_id || student.id || "" )
            if ( key ) map[key] = student
        } )
        return map
    }, [assignedStudents] )

    const aggregatedParticipants = Object.values( participants.reduce( ( acc, current ) => {
        const id = current.student_id
        const score = +current.score || 0

        if ( !acc[id] ) {
            acc[id] = {
                student_name: current.student_name,
                student_id: current.student_id,
                usn: current.student_info?.usn || assignedStudentsByAuid[String( id )]?.usn,
                best_score: current.score,
                max_score: current.max_score,
                total_score: score,
                number_of_attempts: 1
            }
        } else {
            acc[id].number_of_attempts += 1
            acc[id].total_score += score
            if ( current.score > acc[id].best_score ) {
                acc[id].best_score = current.score
            }
        }

        return acc
    }, {} ) ).map( participant => ( {
        ...participant,
        average_score: ( participant.total_score / participant.number_of_attempts ).toFixed( 2 )
    } ) )

    const displayParticipants = useMemo( () => {
        const attemptedIds = new Set( aggregatedParticipants.map( participant => String( participant.student_id ) ) )
        const unattempted = assignedStudents
            .filter( student => !attemptedIds.has( String( student.auid || student.student_id || student.id || "" ) ) )
            .map( student => ( {
                student_name: student.student_name || student.name || "Not Available",
                student_id: student.auid || student.student_id || student.id,
                usn: student.usn,
                best_score: null,
                average_score: null,
                max_score: aggregatedParticipants[0]?.max_score || 0,
                number_of_attempts: 0,
                no_attempt: true,
            } ) )
        return [...aggregatedParticipants, ...unattempted]
    }, [aggregatedParticipants, assignedStudents] )


    const makeSessionLive = async () => {
        try {
            setUpdating( true )
            await api.simbaAI.updatePracticeSession( id, { status: "live" } )
            getData()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while making prctice session live to students." )
        } finally {
            setUpdating( true )
        }
    }

    const getData = useCallback( async () => {
        try {
            setLoading( true )
            const { data: { data: { practice_session, participants } } } = await api.simbaAI.fetchPracticeSession( id )

            setPracticeSession( practice_session )
            setTabValue( prev => prev ? prev : practice_session.status !== "draft" ? 'participants' : "assignedgroups" )
            setParticipants( participants || [] )
            setError( null )

            const groups = practice_session?.access_groups || []
            if ( groups.length > 0 ) {
                try {
                    const studentLists = await Promise.all( groups.map( group => {
                        const params = {}
                        if ( group.section_assignment_id )
                            params.section_assignment_id = group.section_assignment_id
                        else
                            params.batch_assignment_id = group.batch_assignment_id

                        return api.quiz.fetchBatchOrSectionAssignedStudentDetails( params )
                    } ) )

                    const students = studentLists.flatMap( response => {
                        const responseData = response?.data?.data
                        if ( Array.isArray( responseData ) ) return responseData
                        return responseData?.students || []
                    } )

                    if ( students.length === 0 ) {
                        setLegacyGroup( true )
                    }

                    const uniqueStudents = Array.from( new Map( students.map( student => [String( student.auid || student.student_id || student.id ), student] ) ).values() )
                    setAssignedStudents( uniqueStudents )
                } catch ( err ) {
                    console.log( err )
                    setAssignedStudents( [] )
                }
            }
        } catch ( err ) {
            setError( { message: err?.response?.data?.message, code: err?.response?.status || 500 } )
        } finally {
            setLoading( false )
        }
    }, [id] )

    useEffect( () => {
        getData()
    }, [getData] )


    return (
        <Box flexGrow={1} display="flex" padding={layout.pagePadding} gap={layout.sectionsGap} className="slim-custom-scrollbar" flexDirection="column" overflow="auto" >

            {/* Delete Practice Session in Draft MODE */}
            {!loading && practiceSession && practiceSession.status === 'draft' && <ConfirmDialog waiting={deleting} actionName='Delete' message={`The practice sessions cannot be retrieved once deleted.`} confirmAction={deletePracticeSession} cancelAction={() => setDeleteConfirmationDialogState( false )} status={deleteConfirmationDialogState} />}


            {/* Create or Edit Dialog Component */}
            {editMode && practiceSession && <EditPracticeSession
                subject={practiceSession.subject}
                open={editMode}
                onClose={async () => {
                    await getData()
                    setEditMode( false )
                }}
                mode="edit"
                practiceSession={practiceSession}
                participants={participants}
            />}

            {!loading && !error && practiceSession && <Box alignItems="center">
                <BreadcrumbList items={[
                    "Simba AI",
                    { label: "Practice Sessions", link: `/faculty/practice-with-simba` },
                    loading ? <CircularProgress size={14} /> : practiceSession?.title || id
                ]} />
                <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="flex-end" gap="20px">
                    <Box display="flex" alignItems="flex-start" gap="10px" >
                        <Tooltip title="Back to practice sessions">
                            <IconButton color='primary' sx={{ border: `2px solid ${palette.primary.main}` }} size='small' onClick={() => navigate( `/faculty/practice-with-simba` )}>
                                {Icons.default.ArrowBack}
                            </IconButton>
                        </Tooltip>
                        <Box display="flex" flexDirection="column" gap="2px" position="relative"  >
                            <Typography variant="h1" display="flex" alignItems="flex-start" fontWeight="600" component="h1" fontSize="16px">
                                {practiceSession.title}
                                <Typography marginTop="-5px" marginLeft="5px" component="span" padding="2px" borderRadius="5px" textTransform="uppercase" fontSize="10px" fontWeight={500} color={practiceSession.status === 'draft' ? "error" : "success"} bgcolor={practiceSession.status === 'draft' ? palette.error.light + "22" : palette.success.light + "22"} border={`1px dotted ${practiceSession.status === 'draft' ? palette.error.main : palette.success.main}`}  >{practiceSession.status}</Typography>
                            </Typography>
                            <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{practiceSession?.subject?.subject_name} practice session for the topics covered for from {formatDate( practiceSession.date_range_start )} - {formatDate( practiceSession.date_range_end )}</Typography>
                            <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                                {practiceSession.complexity && <CustomChip tooltipTitle='Practice quiz difficulty level' value={practiceSession.complexity} />}
                                {practiceSession.close_date && <CustomChip tooltipTitle='Practice session access closes on' value={`Closes on: ${dayjs( practiceSession.close_date ).format( "LLL" )}`} />}
                                {practiceSession.access_groups.map( ag => {
                                    return (
                                        <CustomChip tooltipTitle={`Assigned ${ag.entity}`} key={ag.id} value={getAccessGroupDetails( ag )} startIcon={Icons.default.Group} />
                                    )
                                } )}
                            </Box>
                        </Box>
                    </Box>
                    <Box display="flex" flexGrow={{ xs: 1, sm: "initial" }} gap="10px">
                        {practiceSession.status === 'draft' && <Button onClick={makeSessionLive} sx={{ flexGrow: { xs: "1", sm: "initial" } }} size='small' color='success' disableElevation disabled={updating} startIcon={updating ? <CircularProgress size={20} /> : Icons.default.Click} variant='contained'>Make it live</Button>}
                        <CustomTooltip disableInteractive title="See how topics are presented to students during practice">
                            <Button onClick={startQuiz} sx={{ flexGrow: { xs: "1", sm: "initial" } }} size='small' color='primary' disableElevation startIcon={Icons.default.QuizModule} variant='contained'>
                                Student Practice Preview
                            </Button>
                        </CustomTooltip>
                        <Button onClick={() => setEditMode( true )} sx={{ flexGrow: { xs: "1", sm: "initial" } }} size='small' color='secondary' startIcon={Icons.default.EditIcon} variant='outlined'>Edit</Button>
                        {practiceSession.status === 'draft' && <Button onClick={() => setDeleteConfirmationDialogState( true )} sx={{ flexGrow: { xs: "1", sm: "initial" } }} size='small' color='error' disableElevation disabled={updating} startIcon={updating ? <CircularProgress size={20} /> : Icons.default.DeleteIconOutlined} variant='outlined'>Delete</Button>}
                    </Box>
                </Box>
            </Box>}

            {!error && practiceSession?.status === "live" && <Box display="grid" gap={layout.cardsGap} width="100%" gridTemplateColumns={{ md: "repeat(auto-fit,minmax(200px,1fr))", xs: "repeat(2,1fr)" }}>
                <CountCard loading={loading} label='Subject' icon={Icons.default.Subjects} value={`${practiceSession?.subject?.subject_name_short} (${practiceSession?.subject?.subject_code})`} />
                <CountCard loading={loading} label='Practices Made' icon={Icons.default.Click} value={participants.length} />
                <CountCard loading={loading} label='Participants' icon={Icons.default.Group} value={Object.keys( participants.reduce( ( acc, attempt ) => {
                    if ( !acc[attempt.student_id] ) acc[attempt.student_id] = true
                    return acc
                }, {} ) ).length} />
                <CountCard loading={loading} label='Total Questions' icon={Icons.default.QuestionMark} value={participants.length * ( participants[0]?.max_score || 0 )} />
                {practiceSession.close_date && <CountCard loading={loading} label={dayjs( practiceSession.close_date ) > dayjs() ? "Closes On" : 'Closed On'} icon={Icons.default.QuestionMark} value={formatDate( practiceSession.close_date )} />}
            </Box>}


            {!loading && !error && practiceSession && practiceSession.status === 'draft' && <Box border={`2px dashed ${palette.error.main}`} padding="20px" borderRadius={layout.borderRadius} bgcolor={palette.error.light + "11"} display="flex" flexDirection={{ md: "row", xs: "column" }} gap="10px" alignItems="center" justifyContent="center"  >
                <Box component="img" src={simbaImg} width="140px" />
                <Box display="flex" flexDirection="column" alignItems={{ md: "flex-start", xs: "center" }}>
                    <Typography textAlign={{ md: "left", xs: "center" }} color='error' variant='h4' fontWeight={800} component="p">This module is in draft mode!</Typography>
                    <Typography textAlign={{ md: "left", xs: "center" }} variant='subtitle2' color='error.light'>This session is currently in draft mode and is hidden from students. Make it live to grant access.</Typography>
                    {/* <Typography textAlign={{ md: "left", xs: "center" }} variant='subtitle2' color='error.light'>Would you like to take a sample practice session?</Typography> */}
                    <Box marginTop="20px" display="flex" gap="10px" alignItems="center" >
                        <CustomTooltip disableInteractive title="See how topics are presented to students during practice">
                            <Button onClick={startQuiz} variant="contained" disableElevation startIcon={Icons.default.QuizModule} size='small' >Student Practice Preview</Button>
                        </CustomTooltip>
                        <Button variant="contained" onClick={makeSessionLive} disableElevation disabled={updating} startIcon={updating ? <CircularProgress size={20} /> : Icons.default.Click} size='small' color='success' >Make it live for students</Button>
                    </Box>
                </Box>
            </Box>}

            {!loading && practiceSession?.modules_and_topics && <Box marginTop="5px" display="flex" flexDirection="column" gap="5px"  >
                {Object.keys( practiceSession.modules_and_topics ).map( ( module, modIndex ) => (
                    <Box border={border[1]} borderRadius={layout.borderRadius} key={module} gap="5px">
                        <Box padding="10px" bgcolor={palette.contentBg} borderBottom={border[1]} >
                            <Typography variant='subtitle2' lineHeight={1.2} color='textSecondary' fontSize="12px"> {practiceSession.subject.subject_name}, {module}</Typography>
                            <Typography variant='subtitle2' component="p" fontSize="14px"> {practiceSession.modules_and_topics[module]?.name}</Typography>
                        </Box>
                        <Box padding="5px" display="flex" flexWrap="wrap" gap="5px">
                            {practiceSession.modules_and_topics[module].topics.map( ( topic, topicIndex ) => (
                                <Box className="animation-wave" border={border[1]} padding="2px 5px" borderRadius={layout.borderRadius} key={topic} display="flex" alignItems="center" gap="5px">
                                    <Typography variant='subtitle2' fontSize="12px"  >{topic}</Typography>
                                    <CustomTooltip placement="top" title={`Learn about ${topic} from Simba AI`} disableInteractive>
                                        <IconButton color='primary' onClick={() => fetchAnswerFromAI( { topic, module, subject_name: practiceSession.subject.subject_name, subject_code: practiceSession.subject.subject_code } )} size='small'>
                                            {Icons.default.AskAI}
                                        </IconButton>
                                    </CustomTooltip>
                                </Box>
                            ) )}
                        </Box>
                    </Box>
                ) )}
            </Box>}

            {legacyGroup && <Typography sx={{ m: 0.5, fontSize: "0.875rem", display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: layout.borderRadius, bgcolor: palette.warning.light + "22", color: palette.warning.main }} >
                <Typography component="span" sx={{ color: "inherit", fontSize: "1.2rem" }} >{Icons.default.Warning}</Typography>
                The batch or section assigned to this practice session belongs to a previous academic year and is no longer active, so the list of students who haven't practiced yet cannot be displayed.
            </Typography>}

            {!loading && !error && <Box border={border[1]} borderRadius={layout.borderRadius}>
                {/* <CustomTabs variant='stretched' styles={{ width: "100%" }} tabs={tabs} handleTabChange={setTabValue} value={tabValue} /> */}

                {!loading && !error && practiceSession && practiceSession.status !== 'draft' && displayParticipants?.length > 0 && <CustomTable
                    rowStyles={rowStylesGetter}
                    customActions={[{
                        type: "button",
                        label: "Export to Excel",
                        startIcon: Icons.default.UploadFileIcon,
                        variant: "outlined",
                        color: "greyed",
                        onClick: rows => participationReport( rows, `${practiceSession.subject.subject_name}-${practiceSession?.title}`, legacyGroup )
                    }]}
                    columns={columns}
                    data={displayParticipants}
                    containerStyles={{
                        minHeight: "300px",
                        maxHeight: "450px",
                        border: "none"
                    }}
                />}

                {!loading && !error && practiceSession && practiceSession.status !== 'draft' && displayParticipants?.length === 0 && <Box padding="20px" borderRadius={layout.borderRadius} bgcolor={palette.contentBg} display="flex" flexDirection="column" gap="10px" alignItems="center" justifyContent="center"  >
                    <Box component="img" src={noParticipationImg} height="180px" />
                    <Typography color='textSecondary' variant='subtitle2'>This practice session has no participants.</Typography>
                </Box>}
            </Box>}

            {loading && !error && <DataLoadingSpinner padding="0" waitingMessage='Loading practice session details...' />}
            {error && <Box flexGrow={1} display="flex" alignItems="center" justifyContent="center">
                <TryAgainMessageBlock err={error.message} code={error.code} getData={getData} loading={loading} />
            </Box>}


            {/* Sample AI Response Checker for Faculty */}
            <SimbaAIForFaculty />
        </Box>
    )
}

export default PracticeSession
